/**
 * OData query parameter interface for SDK list methods
 * Maps to the positional parameters used by generated SDK APIs
 */
export interface ODataQueryParams {
  $expand?: string
  $filter?: string
  $orderby?: string
  $search?: string
  $select?: string
  $skip?: number
  $top?: number
  select?: string
  getNested?: boolean | string
  limit?: number
  normalize?: string
  offset?: number
}

/**
 * Converts OData query parameters object to positional array for SDK list methods
 * This eliminates the need to pass many undefined positional parameters
 *
 * @param params - Object with OData query parameters
 * @returns Array of positional parameters matching SDK method signature
 *
 * @example
 * ```typescript
 * const response = await api.projectsList(
 *   ...buildODataQueryParams({
 *     $orderby: "name",
 *     fields: "id,name",
 *     getNested: true
 *   })
 * )
 * ```
 */
export function buildODataQueryParams(params: ODataQueryParams = {}): any[] {
  const {
    $expand,
    $filter,
    $orderby,
    $search,
    $select,
    $skip,
    $top = 100000,
    select,
    getNested,
    limit,
    normalize,
    offset
  } = params

  // Convert boolean getNested to string if needed
  let getNestedValue: string | undefined
  if (getNested === true) {
    getNestedValue = "true"
  } else if (getNested === false) {
    getNestedValue = "false"
  } else {
    getNestedValue = getNested as string | undefined
  }

  return [
    $expand,
    $filter,
    $orderby,
    $search,
    $select,
    $skip,
    $top,
    select,
    getNestedValue,
    limit,
    normalize,
    offset
  ]
}

/**
 * Converts OData query parameters to options.params format for Pattern 2 APIs
 * (APIs that only accept $skip, $top, and options.params)
 * This allows Pattern 2 APIs to use the same buildODataQueryParams interface
 *
 * @param params - Object with OData query parameters
 * @returns Object with $skip, $top, and options.params structure
 *
 * @example
 * ```typescript
 * const { skip, top, options } = buildODataQueryParamsForPattern2({
 *   $filter: "project eq '123'",
 *   $select: "id,name",
 *   getNested: true,
 *   $skip: 0,
 *   $top: 100
 * })
 * const response = await api.purchasesList(skip, top, options)
 * ```
 */
export function buildODataQueryParamsForPattern2(params: ODataQueryParams = {}) {
  const {
    $expand,
    $filter,
    $orderby,
    $search,
    $select,
    $skip = 0,
    $top = 100000,
    select,
    getNested,
    limit,
    normalize,
    offset
  } = params

  // Convert boolean getNested to string if needed
  let getNestedValue: string | undefined
  if (getNested === true) {
    getNestedValue = "true"
  } else if (getNested === false) {
    getNestedValue = "false"
  } else {
    getNestedValue = getNested as string | undefined
  }

  // Build params object (excluding $skip and $top which are positional)
  const queryParams: Record<string, any> = {}
  if ($expand !== undefined) queryParams.$expand = $expand
  if ($filter !== undefined) queryParams.$filter = $filter
  if ($orderby !== undefined) queryParams.$orderby = $orderby
  if ($search !== undefined) queryParams.$search = $search
  if ($select !== undefined) queryParams.$select = $select
  if (select !== undefined) queryParams.select = select
  if (getNestedValue !== undefined) queryParams.get_nested = getNestedValue
  if (limit !== undefined) queryParams.limit = limit
  if (normalize !== undefined) queryParams.normalize = normalize
  if (offset !== undefined) queryParams.offset = offset

  return {
    skip: $skip,
    top: $top,
    options: {
      params: queryParams
    }
  }
}
